import type { Configuration, RedirectRequest } from '@azure/msal-browser'
import { LogLevel } from '@azure/msal-browser'

export type AuthMode = 'dev' | 'entra'

/** Build time constant, so the provider tree never changes shape at runtime. */
export const AUTH_MODE: AuthMode = import.meta.env.VITE_AUTH_MODE === 'entra' ? 'entra' : 'dev'

const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? ''
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? ''
const apiClientId = import.meta.env.VITE_ENTRA_API_CLIENT_ID ?? ''

/**
 * The delegated scope the API insists on. The Application ID URI of the API
 * registration is api://<api-client-id>, so the scope is derived rather than
 * configured separately: one fewer value to get wrong.
 */
export const API_SCOPE = apiClientId ? `api://${apiClientId}/access_as_user` : ''

export const msalConfig: Configuration = {
  auth: {
    clientId,
    // Single tenant: SCI staff accounts only, never /common or /organizations.
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    /**
     * False, since 30/08/2026. With this true MSAL navigates back to the URL
     * that started the sign in once it has handled the response, which leaves
     * the sign-in response itself sitting in browser history. Pressing Back
     * then landed on it and the browser offered to resubmit the form, showing
     * "Confirm Form Resubmission" and ERR_CACHE_MISS. QA reported exactly that.
     *
     * There is nothing to navigate back to. The redirect URI is the application
     * root and the application is a single page, so the only route is the one
     * already loaded.
     */
    navigateToLoginRequestUrl: false,
  },
  cache: {
    // Sign in survives a new tab, which is how staff use an internal tool. This
    // is the low friction behaviour SCI-SO settled on. No secret is stored: the
    // SPA is a public client and holds only its own short lived tokens.
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      loggerCallback: (_level, message, containsPii) => {
        if (!containsPii) console.warn(`MSAL: ${message}`)
      },
    },
  },
}

/** Auth code flow with PKCE, which is msal-browser's default for an SPA. */
export const loginRequest: RedirectRequest = {
  scopes: API_SCOPE ? [API_SCOPE] : [],
}

export function isEntraConfigured(): boolean {
  return Boolean(clientId && tenantId && apiClientId)
}
